import React, { createContext, useContext, useState, useEffect } from 'react';
import { translations } from '../data/mock';

const LanguageContext = createContext();

// Country to language mapping
const countryToLanguage = {
  // Turkish
  'TR': 'tr', // Turkey
  'AZ': 'tr', // Azerbaijan
  
  // Spanish
  'ES': 'es', // Spain
  'MX': 'es', // Mexico
  'AR': 'es', // Argentina
  'CO': 'es', // Colombia
  'PE': 'es', // Peru
  'VE': 'es', // Venezuela
  'CL': 'es', // Chile
  'EC': 'es', // Ecuador
  'GT': 'es', // Guatemala
  'CU': 'es', // Cuba
  'BO': 'es', // Bolivia
  'DO': 'es', // Dominican Republic
  'HN': 'es', // Honduras
  'PY': 'es', // Paraguay
  'SV': 'es', // El Salvador
  'NI': 'es', // Nicaragua
  'CR': 'es', // Costa Rica
  'PA': 'es', // Panama
  'UY': 'es', // Uruguay
  'PR': 'es', // Puerto Rico
  
  // French
  'FR': 'fr', // France
  'BE': 'fr', // Belgium
  'CH': 'fr', // Switzerland (multilingual, default to French for simplicity)
  'CA': 'fr', // Canada (Quebec)
  'LU': 'fr', // Luxembourg
  'MC': 'fr', // Monaco
  'SN': 'fr', // Senegal
  'CI': 'fr', // Ivory Coast
  'ML': 'fr', // Mali
  'BF': 'fr', // Burkina Faso
  'NE': 'fr', // Niger
  'TG': 'fr', // Togo
  'BJ': 'fr', // Benin
  'GA': 'fr', // Gabon
  'CG': 'fr', // Congo
  'CD': 'fr', // Democratic Republic of Congo
  'CM': 'fr', // Cameroon
  'MG': 'fr', // Madagascar
  'HT': 'fr', // Haiti
  'MA': 'fr', // Morocco
  'DZ': 'fr', // Algeria
  'TN': 'fr', // Tunisia
};

// Page titles based on language
const pageTitles = {
  en: 'HH Law Office | IP, Corporate & Real Estate Law in Turkey',
  tr: 'HH Hukuk Bürosu | Fikri Mülkiyet, Ticaret ve Gayrimenkul Hukuku',
  es: 'HH Law Office | Propiedad Intelectual, Derecho Mercantil e Inmobiliario en Turquía',
  fr: 'HH Law Office | Propriété Intellectuelle, Droit des Affaires et Immobilier en Turquie'
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState('en');
  const [isDetected, setIsDetected] = useState(false);

  // Update document title when language changes
  useEffect(() => {
    document.title = pageTitles[language] || pageTitles.en;
    
    // Also update the html lang attribute
    document.documentElement.lang = language;
  }, [language]);

  useEffect(() => {
    const detectLanguage = async () => {
      // Check if user has a saved preference
      const savedLanguage = localStorage.getItem('hh-language');
      if (savedLanguage && translations[savedLanguage]) {
        setLanguage(savedLanguage);
        setIsDetected(true);
        return;
      }

      // Try to detect language from IP
      try {
        const response = await fetch('https://ipapi.co/json/', { 
          timeout: 5000 
        });
        const data = await response.json();
        
        if (data && data.country_code) {
          const detectedLang = countryToLanguage[data.country_code] || 'en';
          setLanguage(detectedLang);
          localStorage.setItem('hh-language', detectedLang);
        }
      } catch (error) {
        console.log('Could not detect location, using default language');
      }
      
      setIsDetected(true);
    };

    detectLanguage();
  }, []);

  const t = (key) => {
    const keys = key.split('.');
    let value = translations[language];
    for (const k of keys) {
      value = value?.[k];
    }
    return value || key;
  };

  const changeLanguage = (lang) => {
    setLanguage(lang);
    localStorage.setItem('hh-language', lang);
  };

  return (
    <LanguageContext.Provider value={{ language, changeLanguage, t, isDetected }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
